import React from "react";
import {
  Box,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import SectionFrame from "../SectionFrame";
import VisualStyleSelector from "../VisualStyleSelector";
import { EditableButton, EditableText } from "../InlineEditable";
import EditableImage from "../EditableImage";

function BusinessLogo({ form, device, disabled, onUploadImage, onRemoveImage, inverse }) {
  const isMobile = device === "mobile";
  const compact = isMobile && Boolean(form.theme.compactLogoUrl);
  const path = compact ? "theme.compactLogoUrl" : "theme.logoUrl";
  const value = compact ? form.theme.compactLogoUrl : form.theme.logoUrl;

  return (
    <EditableImage
      value={value}
      label={isMobile ? "Logo móvil" : "Logo principal"}
      height={isMobile ? 48 : 62}
      disabled={disabled}
      onUpload={(file) => onUploadImage(path, file, { sectionKey: "branding", assetKey: compact ? "compact-logo" : "logo" })}
      onRemove={() => onRemoveImage(path)}
      sx={{
        minHeight: isMobile ? 48 : 62,
        width: isMobile ? 132 : 205,
        flexShrink: 0,
        borderRadius: 2,
        bgcolor: inverse ? "rgba(255,255,255,.96)" : "transparent",
        borderColor: inverse ? "rgba(255,255,255,.42)" : "rgba(25,36,47,.18)",
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        "& .editable-image-hint": { fontSize: 10, left: 5, bottom: 4, px: 0.7, py: 0.25 },
      }}
    />
  );
}

function NavLinks({ form, productsEnabled, disabled, onCommit, inverse = false, centered = false }) {
  const color = inverse ? "rgba(255,255,255,.82)" : form.theme.textColor || "#1A1A2E";
  return (
    <Stack direction="row" spacing={0.7} alignItems="center" justifyContent={centered ? "center" : "flex-start"}>
      <EditableButton
        value={form.navigation.homeLabel}
        fallback="Inicio"
        disabled={disabled}
        onSave={(value) => onCommit("navigation.homeLabel", value)}
        sx={{ bgcolor: "transparent", color, px: 1.3, py: 0.7, fontWeight: 850 }}
      />
      {form.navigation.showProductsLink !== false && productsEnabled ? (
        <Stack direction="row" spacing={0.1} alignItems="center">
          <EditableButton
            value={form.navigation.productsLabel}
            fallback="Productos"
            disabled={disabled}
            onSave={(value) => onCommit("navigation.productsLabel", value)}
            sx={{ bgcolor: "transparent", color, px: 1.3, py: 0.7, fontWeight: 850 }}
          />
          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color }} />
        </Stack>
      ) : null}
      {form.navigation.showContactLink !== false && form.sections.showContact !== false ? (
        <EditableButton
          value={form.navigation.contactLabel}
          fallback="Contacto"
          disabled={disabled}
          variant={inverse ? "outlined" : "contained"}
          onSave={(value) => onCommit("navigation.contactLabel", value)}
          sx={{
            bgcolor: inverse ? "rgba(255,255,255,.04)" : form.theme.primaryColor || "#F35A3A",
            color: "#fff",
            borderColor: inverse ? "rgba(255,255,255,.5)" : "transparent",
            borderRadius: 999,
            px: 2,
            py: 0.8,
            fontWeight: 900,
          }}
        />
      ) : null}
    </Stack>
  );
}

function AccountAndCart({ form, productsEnabled, inverse }) {
  const color = inverse ? "#fff" : form.theme.secondaryColor || "#19242F";
  return (
    <Stack direction="row" spacing={0.7} alignItems="center">
      {form.navigation.showCustomerAccess !== false ? (
        <Stack direction="row" spacing={0.4} alignItems="center" sx={{ color }}>
          <PersonOutlineRoundedIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
            {form.navigation.loginLabel || "Iniciar sesión"}
          </Typography>
        </Stack>
      ) : null}
      {productsEnabled && form.navigation.showCart !== false ? (
        <IconButton
          size="small"
          sx={{
            color,
            bgcolor: inverse ? "rgba(255,255,255,.08)" : "rgba(25,36,47,.035)",
            border: "1px solid",
            borderColor: inverse ? "rgba(255,255,255,.18)" : "rgba(25,36,47,.10)",
          }}
        >
          <ShoppingCartOutlinedIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Stack>
  );
}

export default function NavbarSection({
  form,
  active,
  device,
  productsEnabled,
  onSelect,
  onCommit,
  onUploadImage,
  onRemoveImage,
  disabled,
}) {
  const isMobile = device === "mobile";
  const style = form.navigation.style || "style-1";
  const secondary = form.theme.secondaryColor || "#19242F";
  const surface = form.theme.surfaceColor || "#fff";
  const inverse = style === "style-3";
  const enabled = form.sections.showNavbar !== false;

  const toolbar = (
    <Box
      sx={{
        minHeight: isMobile ? 72 : style === "style-2" ? 84 : style === "style-3" ? 82 : 86,
        px: isMobile ? 2 : 4.5,
        pt: isMobile ? 1.1 : 1.25,
        pb: isMobile ? 1.1 : 1.25,
        display: "flex",
        alignItems: "center",
        gap: 2,
        maxWidth: 1540,
        mx: "auto",
      }}
    >
      {isMobile ? (
        <>
          <BusinessLogo form={form} device={device} disabled={disabled} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} inverse={inverse} />
          <Box sx={{ flex: 1 }} />
          {productsEnabled && form.navigation.showCart !== false ? (
            <IconButton size="small" sx={{ color: inverse ? "#fff" : secondary }}><ShoppingCartOutlinedIcon /></IconButton>
          ) : null}
          <IconButton size="small" sx={{ color: inverse ? "#fff" : form.theme.primaryColor || "#F35A3A" }}><MenuRoundedIcon /></IconButton>
        </>
      ) : style === "style-3" ? (
        <Box sx={{ width: "100%", display: "grid", gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)", alignItems: "center", gap: 2.5 }}>
          <NavLinks form={form} productsEnabled={productsEnabled} disabled={disabled} onCommit={onCommit} inverse centered />
          <BusinessLogo form={form} device={device} disabled={disabled} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} inverse />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}><AccountAndCart form={form} productsEnabled={productsEnabled} inverse /></Box>
        </Box>
      ) : (
        <>
          <BusinessLogo form={form} device={device} disabled={disabled} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} inverse={false} />
          <Box sx={{ flex: 1 }} />
          <NavLinks form={form} productsEnabled={productsEnabled} disabled={disabled} onCommit={onCommit} />
          <AccountAndCart form={form} productsEnabled={productsEnabled} inverse={false} />
        </>
      )}
    </Box>
  );

  return (
    <SectionFrame
      id="navbar"
      label="Navbar"
      active={active}
      enabled={enabled}
      onSelect={onSelect}
      onToggle={(next) => onCommit("sections.showNavbar", next)}
    >
      <Box
        sx={{
          position: "relative",
          bgcolor: inverse ? secondary : style === "style-1" ? "rgba(247,248,252,.94)" : surface,
          color: inverse ? "#fff" : form.theme.textColor || "#1A1A2E",
          borderBottom: "1px solid",
          borderColor: inverse ? "rgba(255,255,255,.13)" : form.theme.dividerColor || "rgba(25,36,47,.10)",
          boxShadow: style === "style-2" ? "0 10px 30px rgba(20,25,35,.08)" : "none",
        }}
      >
        <VisualStyleSelector value={style} onChange={(value) => onCommit("navigation.style", value)} inverse={inverse} label="Navbar" />

        {style === "style-2" && !isMobile ? (
          <Box sx={{ bgcolor: secondary, color: "#fff", borderBottom: "1px solid rgba(255,255,255,.1)" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ minHeight: 38, px: 4.5 }}>
              <Stack direction="row" spacing={2.1} alignItems="center" sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.55} alignItems="center"><PhoneOutlinedIcon sx={{ fontSize: 15, color: form.theme.primaryColor }} /><Typography variant="caption" sx={{ fontWeight: 750 }}>{form.business.phone || "Teléfono"}</Typography></Stack>
                <Stack direction="row" spacing={0.55} alignItems="center"><EmailOutlinedIcon sx={{ fontSize: 15, color: form.theme.primaryColor }} /><Typography variant="caption" noWrap>{form.business.email || "correo@negocio.com"}</Typography></Stack>
                <Stack direction="row" spacing={0.55} alignItems="center"><LocationOnOutlinedIcon sx={{ fontSize: 15, color: form.theme.primaryColor }} /><Typography variant="caption" noWrap>{form.business.address || "Dirección del negocio"}</Typography></Stack>
              </Stack>
              {form.navigation.showSocialLinks !== false ? (
                <Stack direction="row" spacing={0.15}>
                  <IconButton size="small" sx={{ color: "#fff" }}><WhatsAppIcon sx={{ fontSize: 17 }} /></IconButton>
                  <IconButton size="small" sx={{ color: "#fff" }}><InstagramIcon sx={{ fontSize: 17 }} /></IconButton>
                  <IconButton size="small" sx={{ color: "#fff" }}><FacebookIcon sx={{ fontSize: 17 }} /></IconButton>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        ) : null}
        {toolbar}
      </Box>
    </SectionFrame>
  );
}
